/**
 * Color Space Comparison Component
 * Educational tools for comparing different color spaces and gamuts
 */

import React, { useState, useCallback, useMemo } from 'react';
import { 
  BarChart3, 
  Target, 
  Info, 
  Eye, 
  Download, 
  RotateCcw,
  TrendingUp,
  BookOpen
} from 'lucide-react';
import { 
  COLOR_SPACES,
  generateGamutBoundary,
  compareColorSpaces,
  getGamutExplanation,
  getGamutExamples,
  type ColorSpace,
  type GamutComparison
} from '../../lib/education/gamutUtils';

export type ComparisonMode = 'visual' | 'technical' | 'practical' | 'examples';

interface ColorSpaceComparisonProps {
  initialSpaces?: [string, string];
  onComparisonComplete?: (result: GamutComparison) => void;
  className?: string;
}

export function ColorSpaceComparison({
  initialSpaces = ['sRGB', 'DisplayP3'],
  onComparisonComplete,
  className = ''
}: ColorSpaceComparisonProps) {
  const [mode, setMode] = useState<ComparisonMode>('visual');
  const [primarySpace, setPrimarySpace] = useState<ColorSpace>(COLOR_SPACES[initialSpaces[0]]);
  const [secondarySpace, setSecondarySpace] = useState<ColorSpace>(COLOR_SPACES[initialSpaces[1]]);
  const [comparisonResult, setComparisonResult] = useState<GamutComparison | null>(null);

  // Generate comparison data and update state
  useMemo(() => {
    const comparison = compareColorSpaces(primarySpace, secondarySpace);
    setComparisonResult(comparison);
    onComparisonComplete?.(comparison);
  }, [primarySpace, secondarySpace, onComparisonComplete]);

  // Generate individual boundaries
  const primaryBoundary = useMemo(() => {
    return generateGamutBoundary(primarySpace);
  }, [primarySpace]);

  const secondaryBoundary = useMemo(() => {
    return generateGamutBoundary(secondarySpace);
  }, [secondarySpace]);

  // Get comparison insights
  const getComparisonInsights = useCallback((comparison: GamutComparison) => {
    const insights: string[] = [];
    
    if (comparison.overlap > 80) {
      insights.push('These color spaces have very similar gamuts with high compatibility.');
    } else if (comparison.overlap > 50) {
      insights.push('Moderate overlap with some unique colors in each space.');
    } else {
      insights.push('These color spaces have significant differences in color reproduction capabilities.');
    }

    if (primaryBoundary.coverage > secondaryBoundary.coverage) {
      insights.push(`${primarySpace.name} provides approximately ${Math.round(primaryBoundary.coverage - secondaryBoundary.coverage)}% more color coverage.`);
    } else if (secondaryBoundary.coverage > primaryBoundary.coverage) {
      insights.push(`${secondarySpace.name} provides approximately ${Math.round(secondaryBoundary.coverage - primaryBoundary.coverage)}% more color coverage.`);
    }

    // Use case recommendations
    if (primarySpace.name === 'sRGB' && secondarySpace.name === 'Display P3') {
      insights.push('Display P3 is ideal for modern displays and vibrant content, while sRGB ensures maximum compatibility.');
    } else if (primarySpace.name === 'Adobe RGB' && secondarySpace.name === 'sRGB') {
      insights.push('Adobe RGB is better for professional printing workflows, while sRGB is preferred for web content.');
    } else if (primarySpace.name === 'Rec. 2020') {
      insights.push('Rec. 2020 represents future color capabilities but current devices cannot display its full gamut.');
    }

    return insights;
  }, [primarySpace, secondarySpace, primaryBoundary, secondaryBoundary]);

  // Export comparison data
  const exportComparison = useCallback(() => {
    if (!comparisonResult) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      primary: {
        name: comparisonResult.primary.name,
        coverage: primaryBoundary.coverage,
        description: comparisonResult.primary.description
      },
      secondary: {
        name: comparisonResult.secondary.name,
        coverage: secondaryBoundary.coverage,
        description: comparisonResult.secondary.description
      },
      overlap: comparisonResult.overlap,
      insights: getComparisonInsights(comparisonResult)
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `color-space-comparison-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [comparisonResult, primaryBoundary, secondaryBoundary, getComparisonInsights]);

  // Reset comparison
  const resetComparison = useCallback(() => {
    setPrimarySpace(COLOR_SPACES.sRGB);
    setSecondarySpace(COLOR_SPACES.DisplayP3);
  }, []);

  // Get color space examples
  const colorSpaceExamples = getGamutExamples();

  return (
    <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}>
      {/* Header */}
      <header>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Color Space Comparison
          </h3>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Educational Analysis
            </span>
          </div>
        </div>

        {/* Mode selector */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'visual' as ComparisonMode, label: 'Visual', icon: Eye },
            { id: 'technical' as ComparisonMode, label: 'Technical', icon: Target },
            { id: 'practical' as ComparisonMode, label: 'Practical', icon: TrendingUp },
            { id: 'examples' as ComparisonMode, label: 'Examples', icon: BookOpen }
          ].map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                mode === id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {React.createElement(icon, { className: "w-4 h-4" })}
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Color space selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Primary Color Space</label>
          <select
            value={primarySpace.name}
            onChange={(e) => setPrimarySpace(COLOR_SPACES[e.target.value])}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          >
            {Object.entries(COLOR_SPACES).map(([key, space]) => (
              <option key={key} value={key}>{space.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {primarySpace.description}
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Secondary Color Space</label>
          <select
            value={secondarySpace.name}
            onChange={(e) => setSecondarySpace(COLOR_SPACES[e.target.value])}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          >
            {Object.entries(COLOR_SPACES).map(([key, space]) => (
              <option key={key} value={key}>{space.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {secondarySpace.description}
          </p>
        </div>
      </div>

      {/* Comparison results */}
      {comparisonResult && (
        <div className="space-y-6">
          {/* Coverage comparison */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {primaryBoundary.coverage.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{primarySpace.name} Coverage</div>
            </div>
            
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {secondaryBoundary.coverage.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{secondarySpace.name} Coverage</div>
            </div>
            
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {comparisonResult.overlap.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Overlap</div>
            </div>
            
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {Math.abs(primaryBoundary.coverage - secondaryBoundary.coverage).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Difference</div>
            </div>
          </div>

          {/* Mode-specific content */}
          {mode === 'visual' && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">Visual Comparison</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400">{primarySpace.name}</h5>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Gamut Area</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {primaryBoundary.area.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Coverage</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {primaryBoundary.coverage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400">{secondarySpace.name}</h5>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Gamut Area</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {secondaryBoundary.area.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Coverage</span>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {secondaryBoundary.coverage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === 'technical' && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">Technical Specifications</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400">{primarySpace.name} Primaries</h5>
                  <div className="space-y-2">
                    {Object.entries(primarySpace.primaries).map(([color, coords]) => (
                      <div key={color} className="flex items-center justify-between text-xs">
                        <span className="capitalize text-gray-600 dark:text-gray-400">{color}</span>
                        <span className="font-mono text-gray-700 dark:text-gray-300">
                          x: {coords.x.toFixed(3)}, y: {coords.y.toFixed(3)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Gamma</span>
                      <span className="font-mono text-gray-700 dark:text-gray-300">
                        {primarySpace.gamma}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400">{secondarySpace.name} Primaries</h5>
                  <div className="space-y-2">
                    {Object.entries(secondarySpace.primaries).map(([color, coords]) => (
                      <div key={color} className="flex items-center justify-between text-xs">
                        <span className="capitalize text-gray-600 dark:text-gray-400">{color}</span>
                        <span className="font-mono text-gray-700 dark:text-gray-300">
                          x: {coords.x.toFixed(3)}, y: {coords.y.toFixed(3)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-600 dark:text-gray-400">Gamma</span>
                      <span className="font-mono text-gray-700 dark:text-gray-300">
                        {secondarySpace.gamma}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === 'practical' && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">Practical Insights</h4>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200">Key Differences</h5>
                  <ul className="space-y-2">
                    {getComparisonInsights(comparisonResult).map((insight, index) => (
                      <li key={index} className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
                        <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {primarySpace.name} Best For:
                  </h5>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {getGamutExplanation(primarySpace)}
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {secondarySpace.name} Best For:
                  </h5>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {getGamutExplanation(secondarySpace)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {mode === 'examples' && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">Real-World Examples</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {colorSpaceExamples.slice(0, 4).map((example, index) => (
                  <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {example.name}
                    </h5>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {example.description}
                    </p>
                    
                    <div className="flex gap-2 mb-2">
                      {example.colors.slice(0, 4).map((color, colorIndex) => (
                        <div
                          key={colorIndex}
                          className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Optimized for: {example.colorSpace}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {example.explanation}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={exportComparison}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Export Comparison
            </button>
            <button
              onClick={resetComparison}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4 inline mr-2" />
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Educational note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <strong>Understanding Color Spaces:</strong> Different color spaces are optimized for specific use cases. 
            sRGB ensures web compatibility, Display P3 provides vibrant colors for modern displays, 
            Adobe RGB serves professional printing workflows, and Rec. 2020 represents future color capabilities.
          </div>
        </div>
      </div>
    </div>
  );
}
